import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QuestionCatalog } from '@/services/questionCatalogs';
import { useAuth } from '@/hooks/useClerkAuth';
import { fetchQuestionCatalogs } from '@/services/questionCatalogs';
import TopNavBar from '@/components/TopNavBar';
import SEO from '@/hooks/useSEO';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Play, Edit, Plus, Book, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

const Questions = () => {
  const { t } = useTranslation();
  const [catalogs, setCatalogs] = useState<QuestionCatalog[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const { user, isLoading, canCreateQuestionCatalog, canEditQuestionCatalog } =
    useAuth();
  const navigate = useNavigate();

  const currentView =
    searchParams.get('view') === 'admin' ? 'admin' : 'catalogs';

  // Load question catalogs from Supabase
  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        setLoadingCatalogs(true);
        const fetchedCatalogs = await fetchQuestionCatalogs();
        setCatalogs(fetchedCatalogs);
      } catch (error) {
        console.error('Error loading question catalogs:', error);
      } finally {
        setLoadingCatalogs(false);
      }
    };

    if (!isLoading) {
      loadCatalogs();
    }
  }, [isLoading]);

  const navigateToAdmin = () => {
    if (!user || !canCreateQuestionCatalog()) {
      navigate('/sign-in');
      return;
    }
    setSearchParams({ view: 'admin' });
  };

  const navigateBack = () => {
    setSearchParams({});
  };

  const handleStartPractice = (catalogId: string) => {
    if (!user) {
      navigate('/sign-in');
      return;
    }
    navigate(`/questions/${catalogId}/practice`);
  };

  const handleEditCatalog = (catalogId: string) => {
    if (!user || !canEditQuestionCatalog()) {
      navigate('/sign-in');
      return;
    }
    navigate(`/questions/${catalogId}/edit`);
  };

  const renderCatalogCard = (catalog: QuestionCatalog) => (
    <Card
      key={catalog.id}
      className="group hover:shadow-lg transition-all duration-300 border border-learning-muted/40 hover:border-learning-primary/40"
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-xl mb-2 group-hover:text-learning-primary transition-colors">
              {catalog.name}
            </CardTitle>
            {catalog.description && (
              <CardDescription className="text-muted-foreground">
                {catalog.description}
              </CardDescription>
            )}
          </div>
          {catalog.imageUrl && (
            <img
              src={catalog.imageUrl}
              alt={catalog.name}
              className="w-16 h-16 rounded-lg object-cover ml-4"
            />
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Book className="h-4 w-4" />
            <span>
              {catalog.totalQuestions} {t('articles.questions')}
            </span>
          </div>
          {catalog.stateSpecificQuestions > 0 && (
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>
                {catalog.stateSpecificQuestions} {t('articles.stateSpecific')}
              </span>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-2">
          <Button
            onClick={() => handleStartPractice(catalog.id)}
            className="flex-1 bg-learning-primary hover:bg-learning-primary/90"
          >
            <Play className="h-4 w-4 mr-2" />
            {t('articles.startPractice')}
          </Button>

          {canEditQuestionCatalog() && (
            <Button
              variant="outline"
              onClick={() => handleEditCatalog(catalog.id)}
              className="border-learning-muted hover:border-learning-primary hover:text-learning-primary"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderLoadingSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map(i => (
        <Card key={i} className="border border-learning-muted/40">
          <CardHeader className="pb-3">
            <Skeleton className="h-6 w-3/4 mb-2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-20" />
            </div>
            <div className="flex gap-3">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-12" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  if (currentView === 'admin') {
    return (
      <div className="min-h-screen bg-gradient-hero">
        <TopNavBar />
        <div className="container max-w-7xl mx-auto p-6">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-primary-foreground mb-2">
                {t('questions.catalogAdmin')}
              </h1>
              <p className="text-primary-foreground/90">
                {t('questions.catalogAdminDesc')}
              </p>
            </div>
            <Button
              onClick={navigateBack}
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              {t('questions.backToCatalogs')}
            </Button>
          </div>

          <Card className="bg-white/95 backdrop-blur-sm border-0">
            <CardContent className="p-8">
              <div className="text-center space-y-4">
                <div className="mx-auto w-16 h-16 bg-learning-primary/10 rounded-full flex items-center justify-center">
                  <Plus className="h-8 w-8 text-learning-primary" />
                </div>
                <h3 className="text-2xl font-bold">
                  {t('questions.createQuestionCatalog')}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {t('questions.addNewCatalogDesc')}
                </p>
                <Button
                  size="lg"
                  className="bg-learning-primary hover:bg-learning-primary/90"
                  onClick={() => navigate('/questions/new/edit')}
                >
                  <Plus className="h-5 w-5 mr-2" />
                  {t('questions.createNewCatalog')}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-hero">
      <SEO
        title="Practice Tests – Leben in Deutschland &amp; More"
        description="Practice German citizenship tests including Leben in Deutschland. Choose a question catalog and start practicing with instant translations and explanations."
        canonicalPath="/questions"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: 'German Practice Test Catalogs',
          description:
            'Question catalogs for German citizenship and language tests, including Leben in Deutschland.',
          url: 'https://linguacraft.de/questions',
          itemListElement: catalogs.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.name,
            url: `https://linguacraft.de/questions/${c.id}/practice`,
          })),
        }}
      />
      <TopNavBar
        currentPage="questions"
        onNavigateToArticles={() => navigate('/')}
        onNavigateToDashboard={() => navigate('/dashboard')}
        onNavigateToAdmin={() => setSearchParams({ view: 'admin' })}
        onNavigateToQuestions={() => navigate('/questions')}
      />
      <div className="container max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-primary-foreground mb-2">
              {t('articles.practiceQuestions')}
            </h1>
            <p className="text-primary-foreground/90">
              {t('questions.practiceDesc')}
            </p>
          </div>

          {canCreateQuestionCatalog() && (
            <Button
              onClick={navigateToAdmin}
              variant="outline"
              className="bg-white/10 text-white border-white/20 hover:bg-white/20"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('questions.admin')}
            </Button>
          )}
        </div>

        {/* Main Content */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-white/20 p-8">
          {loadingCatalogs ? (
            renderLoadingSkeleton()
          ) : catalogs.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-16 h-16 bg-learning-muted/20 rounded-full flex items-center justify-center mb-6">
                <Book className="h-8 w-8 text-learning-muted" />
              </div>
              <h3 className="text-xl font-semibold mb-2">
                {t('questions.noCatalogsYet')}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                {t('questions.noCatalogsDesc')}
              </p>
              {canCreateQuestionCatalog() && (
                <Button
                  onClick={navigateToAdmin}
                  className="bg-learning-primary hover:bg-learning-primary/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('questions.createFirstCatalog')}
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {catalogs.map(renderCatalogCard)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Questions;
